/* ==========================================================================
   BuildDirect — Landing Page Sample Picker
   --------------------------------------------------------------------------
   State model: the Shopify cart IS the state. We never keep a parallel store.
   That means selections survive navigation, refresh and the trip to checkout,
   and the tray can never desync from what the customer will actually receive.

   A "free sample" is a $0.00 variant whose SKU ends in the sample suffix
   (default "-S"). Paid XL samples ("-L", $25) are deliberately NOT counted
   against the free-sample cap — they are the upsell path, not the promise.

   The cap itself comes from Shopify data (custom.sample_product_quantity_limit),
   passed down by the section. It is never hard-coded here.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lpSamplePickerInit) return;
  window.__lpSamplePickerInit = true;

  var SAMPLE_SUFFIX = '-S';
  var ATTR_SESSION_KEY = 'lp_campaign_context';
  var ATTR_SYNCED_KEY = 'lp_campaign_context_synced';

  /* ------------------------------------------------------------- helpers */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function isFreeSampleLine(line) {
    return line && line.price === 0 && typeof line.sku === 'string' && line.sku.slice(-2) === SAMPLE_SUFFIX;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
        return data;
      });
    });
  }

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event };
    var context = campaignContext();
    for (var k in context) if (Object.prototype.hasOwnProperty.call(context, k)) data[k] = context[k];
    for (var j in payload) if (Object.prototype.hasOwnProperty.call(payload, j)) data[j] = payload[j];
    window.dataLayer.push(data);
  }

  /* --------------------------------------------------- campaign context
     Captured once per session from the ad click, then replayed onto every
     event and written to cart attributes so it lands on the Shopify order
     and flows through to HubSpot. Strategy p.17.                          */

  var _context = null;

  function campaignContext() {
    if (_context) return _context;

    var stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(ATTR_SESSION_KEY) || 'null');
    } catch (e) {
      stored = null;
    }

    var params = new URLSearchParams(window.location.search);
    var root = document.querySelector('[data-lp-page]');
    var fresh = {
      theme_id: root ? root.getAttribute('data-lp-page') : '',
      landing_page_version: root ? root.getAttribute('data-lp-version') || '1' : '1',
      ad_concept: params.get('ad_concept') || params.get('utm_content') || '',
      creative_id: params.get('creative_id') || params.get('utm_term') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || ''
    };

    // A fresh ad click wins; otherwise keep what the session already captured
    // so an internal navigation doesn't blank the attribution.
    if (stored) {
      for (var key in fresh) {
        if (!fresh[key] && stored[key]) fresh[key] = stored[key];
      }
    }

    _context = fresh;
    try {
      sessionStorage.setItem(ATTR_SESSION_KEY, JSON.stringify(fresh));
    } catch (e) {
      /* private mode — events still carry context, cart attributes still sync */
    }
    return _context;
  }

  function markSynced() {
    try {
      sessionStorage.setItem(ATTR_SYNCED_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }

  // options.beacon — the caller is about to navigate away. A fetch started on
  // an unloading page is cancelled; sendBeacon survives it. Used when the
  // customer leaves for the full range, because this script does not run on
  // collection or product pages and a sample added there would otherwise reach
  // the order with no campaign attribution on it at all.
  function syncCartAttributes(options) {
    // Only needs to happen once per session, and only once there is a cart.
    try {
      if (sessionStorage.getItem(ATTR_SYNCED_KEY) === '1') return Promise.resolve();
    } catch (e) {
      /* ignore */
    }
    var context = campaignContext();
    var attributes = {};
    for (var key in context) {
      if (Object.prototype.hasOwnProperty.call(context, key) && context[key]) {
        attributes[key] = context[key];
      }
    }
    if (!Object.keys(attributes).length) return Promise.resolve();

    if (options && options.beacon && navigator.sendBeacon) {
      var payload = new Blob([JSON.stringify({ attributes: attributes })], { type: 'application/json' });
      // Only claim it synced if the browser accepted the beacon — a false
      // return would otherwise lose the attribution for the whole session.
      if (navigator.sendBeacon('/cart/update.js', payload)) {
        markSynced();
        return Promise.resolve();
      }
    }

    return postJSON('/cart/update.js', { attributes: attributes })
      .then(markSynced)
      .catch(function () {
        /* attribution is best-effort; never block a sample add on it */
      });
  }

  /* ---------------------------------------------------------------- core */

  function Picker() {
    this.cartLines = [];
    this.busy = false;
    this.queue = Promise.resolve();
    this.pickers = $$('[data-lp-picker]');
    this.tray = $('[data-lp-tray]');
    this.sticky = $('[data-lp-sticky]');
    this.cap = this.resolveCap();
  }

  Picker.prototype.resolveCap = function () {
    // Shopify data wins: the tray carries the cap resolved from
    // custom.sample_product_quantity_limit on the sample variants actually
    // rendered. The section setting is only a fallback for when that
    // metafield is unset.
    if (this.tray) {
      var fromData = parseInt(this.tray.getAttribute('data-lp-cap'), 10);
      if (!isNaN(fromData) && fromData > 0) return fromData;
    }

    var declared = this.pickers
      .map(function (el) {
        return parseInt(el.getAttribute('data-lp-max'), 10);
      })
      .filter(function (n) {
        return !isNaN(n) && n > 0;
      });
    return declared.length ? Math.min.apply(null, declared) : 5;
  };

  Picker.prototype.init = function () {
    var self = this;
    this.bind();
    this.refresh();

    push('lp_view', {
      lp_sample_cap: this.cap,
      // Count cards, not [data-sample-variant-id] — that attribute is on both
      // the card and its button, which double-counted the grid.
      lp_products_shown: $$('[data-lp-card]').length
    });

    // Another surface (PDP button, cart drawer) may have changed the cart.
    document.addEventListener('dispatch:cart-drawer:refresh', function () {
      self.refresh();
    });
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) self.refresh();
    });
  };

  Picker.prototype.bind = function () {
    var self = this;

    document.addEventListener('click', function (e) {
      var addBtn = e.target.closest('[data-lp-add]');
      if (addBtn) {
        e.preventDefault();
        self.toggle(addBtn);
        return;
      }

      var removeBtn = e.target.closest('[data-lp-remove]');
      if (removeBtn) {
        e.preventDefault();
        self.remove(removeBtn.getAttribute('data-lp-remove'));
        return;
      }

      var reviewBtn = e.target.closest('[data-lp-review]');
      if (reviewBtn) {
        push('sample_tray_review', { lp_samples_selected: self.selectedCount() });
        return;
      }

      // Leaving the shortlist for the full catalogue. Measured separately from
      // the expert CTA: it is the signal that the curated grid did not carry
      // the choice, and it is how we find out whether 12 cards is enough.
      var browseLink = e.target.closest('[data-lp-browse]');
      if (browseLink) {
        push('sample_browse_all', {
          lp_cta_location: browseLink.getAttribute('data-lp-browse') || 'unknown',
          lp_samples_selected: self.selectedCount(),
          lp_products_shown: $$('[data-lp-card]').length
        });
        syncCartAttributes({ beacon: true });
        return;
      }

      var expertLink = e.target.closest('[data-lp-expert-cta]');
      if (expertLink) {
        push('expert_contact_click', {
          lp_cta_location: expertLink.getAttribute('data-lp-expert-cta') || 'unknown',
          lp_samples_selected: self.selectedCount()
        });
      }
    });

    // Need / attribute filter chips.
    document.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-lp-filter]');
      if (!chip) return;
      e.preventDefault();
      var pressed = chip.getAttribute('aria-pressed') === 'true';
      var group = chip.closest('[data-lp-filter-group]');

      if (group && group.getAttribute('data-lp-filter-mode') === 'single') {
        $$('[data-lp-filter]', group).forEach(function (other) {
          other.setAttribute('aria-pressed', 'false');
        });
      }
      chip.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      self.applyFilters();
      push('sample_filter', {
        lp_filter: chip.getAttribute('data-lp-filter'),
        lp_filter_active: !pressed
      });
    });
  };

  /* ------------------------------------------------------------ filtering */

  Picker.prototype.applyFilters = function () {
    var active = $$('[data-lp-filter][aria-pressed="true"]').map(function (chip) {
      return chip.getAttribute('data-lp-filter');
    });

    this.pickers.forEach(function (picker) {
      var visible = 0;
      $$('[data-lp-card]', picker).forEach(function (card) {
        var needs = (card.getAttribute('data-lp-needs') || '').split(' ').filter(Boolean);
        // Intersection semantics: a card must satisfy every active filter.
        var match = active.every(function (filter) {
          return needs.indexOf(filter) !== -1;
        });
        card.hidden = !match;
        if (match) visible++;
      });

      var empty = $('[data-lp-empty]', picker);
      if (empty) empty.hidden = visible !== 0;

      var counter = $('[data-lp-visible-count]', picker);
      if (counter) counter.textContent = String(visible);
    });
  };

  /* -------------------------------------------------------- cart syncing */

  Picker.prototype.refresh = function () {
    var self = this;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        self.cartLines = (cart.items || []).filter(isFreeSampleLine);
        self.render();
        return cart;
      })
      .catch(function () {
        /* leave last known state on screen rather than clearing selections */
      });
  };

  Picker.prototype.selectedCount = function () {
    return this.cartLines.length;
  };

  Picker.prototype.lineFor = function (variantId) {
    for (var i = 0; i < this.cartLines.length; i++) {
      if (String(this.cartLines[i].id) === String(variantId)) return this.cartLines[i];
    }
    return null;
  };

  /* ------------------------------------------------------------- actions */

  Picker.prototype.toggle = function (btn) {
    var card = btn.closest('[data-lp-card]');
    var sampleVariantId = btn.getAttribute('data-sample-variant-id') || (card && card.getAttribute('data-sample-variant-id'));
    if (!sampleVariantId) return;

    var existing = this.lineFor(sampleVariantId);
    if (existing) {
      this.remove(existing.key, card);
      return;
    }

    if (this.selectedCount() >= this.cap) {
      this.showCapMessage();
      return;
    }
    this.add(sampleVariantId, btn, card);
  };

  Picker.prototype.add = function (variantId, btn, card) {
    var self = this;
    btn.classList.add('is-busy');
    this.clearError();

    this.queue = this.queue
      .then(function () {
        // Re-check against live cart state: another tab or the PDP may have
        // filled the last slot while this page was open.
        return self.refresh();
      })
      .then(function () {
        if (self.lineFor(variantId)) return null;
        if (self.selectedCount() >= self.cap) {
          self.showCapMessage();
          return null;
        }
        // No line item properties: campaign attribution rides on cart
        // attributes (see syncCartAttributes) so it lands on the order once
        // rather than repeating on every sample line, where Shopify Admin
        // renders it as `</> _lp_theme` against each item.
        return window.BDSampleQuantity.add([{ id: Number(variantId), quantity: 1 }]).then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || 'Cart request failed');
            return data;
          });
        });
      })
      .then(function (result) {
        if (!result) return null;
        return syncCartAttributes().then(function () {
          return self.refresh();
        });
      })
      .then(function () {
        if (self.lineFor(variantId)) {
          push('sample_add', {
            lp_variant_id: String(variantId),
            lp_product: card ? card.getAttribute('data-lp-title') || '' : '',
            lp_samples_selected: self.selectedCount(),
            lp_sample_cap: self.cap
          });
        }
      })
      .catch(function (err) {
        self.showError(err && err.message ? err.message : 'We could not add that sample. Please try again.');
      })
      .then(function () {
        btn.classList.remove('is-busy');
      });

    return this.queue;
  };

  Picker.prototype.remove = function (lineKey, card) {
    var self = this;
    this.clearError();

    this.queue = this.queue
      .then(function () {
        return postJSON('/cart/change.js', { id: lineKey, quantity: 0 });
      })
      .then(function () {
        return self.refresh();
      })
      .then(function () {
        push('sample_remove', {
          lp_product: card ? card.getAttribute('data-lp-title') || '' : '',
          lp_samples_selected: self.selectedCount(),
          lp_sample_cap: self.cap
        });
      })
      .catch(function (err) {
        self.showError(err && err.message ? err.message : 'We could not remove that sample. Please try again.');
      });

    return this.queue;
  };

  /* -------------------------------------------------------------- render */

  Picker.prototype.render = function () {
    var self = this;
    var count = this.selectedCount();
    var full = count >= this.cap;

    // Card states
    $$('[data-lp-card]').forEach(function (card) {
      var variantId = card.getAttribute('data-sample-variant-id');
      var line = self.lineFor(variantId);
      var btn = $('[data-lp-add]', card);
      var selected = !!line;

      card.classList.toggle('is-selected', selected);
      if (!btn) return;

      var label = card.getAttribute('data-lp-title') || 'this sample';
      btn.textContent = selected ? 'Selected ✓' : 'Add free sample';
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      btn.setAttribute(
        'aria-label',
        (selected ? 'Remove ' : 'Add free sample: ') + label
      );
      // Cap only blocks NEW selections — never traps an existing one.
      btn.setAttribute('aria-disabled', !selected && full ? 'true' : 'false');
    });

    this.pickers.forEach(function (picker) {
      picker.classList.toggle('is-full', full);
      var countEl = $('[data-lp-count]', picker);
      if (countEl) countEl.textContent = count + ' of ' + self.cap + ' selected';
    });

    this.renderTray(count, full);
  };

  Picker.prototype.renderTray = function (count, full) {
    if (!this.tray) return;

    var statusEl = $('[data-lp-tray-status]', this.tray);
    var hintEl = $('[data-lp-tray-hint]', this.tray);
    var thumbsEl = $('[data-lp-tray-thumbs]', this.tray);

    this.tray.classList.toggle('is-visible', count > 0);
    // The tray replaces the plain sticky CTA once selection has begun.
    if (this.sticky) this.sticky.classList.toggle('is-superseded', count > 0);

    if (statusEl) statusEl.textContent = count + ' of ' + this.cap + ' selected';
    if (hintEl) {
      hintEl.textContent = full
        ? 'That’s your five — review and order'
        : 'Add up to ' + (this.cap - count) + ' more, free';
    }

    if (thumbsEl) {
      thumbsEl.innerHTML = '';
      this.cartLines.forEach(function (line) {
        var li = document.createElement('li');
        li.className = 'lp-tray__thumb';

        if (line.image) {
          var img = document.createElement('img');
          img.src = line.image.replace(/(\.[a-z]+)(\?|$)/i, '_80x80$1$2');
          img.alt = '';
          img.loading = 'lazy';
          img.width = 40;
          img.height = 40;
          li.appendChild(img);
        }

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lp-tray__remove';
        btn.setAttribute('data-lp-remove', line.key);
        btn.setAttribute('aria-label', 'Remove ' + (line.product_title || 'sample'));
        btn.textContent = '×';
        li.appendChild(btn);

        thumbsEl.appendChild(li);
      });
    }

    // Keep the page scrollable past the tray. Measured rather than assumed —
    // the tray is taller on mobile, where the thumbs take their own row.
    var self2 = this;
    requestAnimationFrame(function () {
      var h = count > 0 ? self2.tray.offsetHeight : 0;
      document.documentElement.style.setProperty('--lp-tray-h', h + 'px');
    });
  };

  /* ------------------------------------------------------------ messaging */

  Picker.prototype.showCapMessage = function () {
    this.showError(
      'You’ve chosen your ' +
        this.cap +
        ' free samples. Remove one to swap it, or order these and your flooring expert can send more.'
    );
  };

  Picker.prototype.showError = function (message) {
    $$('[data-lp-error]').forEach(function (el) {
      el.textContent = message;
      el.hidden = false;
    });
  };

  Picker.prototype.clearError = function () {
    $$('[data-lp-error]').forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
  };

  /* ---------------------------------------------------------------- boot */

  function boot() {
    if (!document.querySelector('[data-lp-page]')) return;
    var picker = new Picker();
    window.lpSamplePicker = picker;
    picker.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

